/**
 * Battery-optimisation helpers.
 *
 * Android OEMs (Xiaomi/MIUI, Vivo, Oppo, OnePlus, Samsung, Realme, Honor)
 * aggressively kill background services to save battery. Even though our
 * GPS plugin runs as a foreground service, these vendors override Android's
 * standard behaviour and stop the process anyway, causing GPS gaps.
 *
 * This file exposes:
 *   • detectVendor()        — best-effort device brand detection from UA
 *   • openBatteryOptIgnore()— opens the OS "Battery optimisation" page
 *                              where the user can mark Carloo Tex as
 *                              "Not optimised / Don't optimise"
 *   • openAutostart()       — opens the OEM-specific autostart whitelist
 *                              page where supported
 *   • openAppDetails()      — opens the standard App Info page (last resort)
 *
 * Implementation note: instead of writing a custom Capacitor plugin we use
 * Android's `intent://` URI scheme, which Capacitor's WebView (Chrome WebView)
 * resolves natively. This works without any Gradle changes.
 */

const APP_PKG = 'com.carloo.tex';

const isAndroid = () =>
  typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent || '');

export function detectVendor() {
  if (!isAndroid()) return 'other';
  const ua = (navigator.userAgent || '').toLowerCase();
  if (/xiaomi|redmi|poco|mi /.test(ua)) return 'xiaomi';
  if (/vivo/.test(ua)) return 'vivo';
  if (/oppo/.test(ua)) return 'oppo';
  if (/oneplus/.test(ua)) return 'oneplus';
  if (/realme/.test(ua)) return 'realme';
  if (/samsung|sm-/.test(ua)) return 'samsung';
  if (/honor|huawei/.test(ua)) return 'honor';
  return 'android';
}

const launch = (uri) => {
  if (typeof window === 'undefined') return false;
  try {
    window.location.href = uri;
    return true;
  } catch (_) {
    return false;
  }
};

export function openBatteryOptIgnore() {
  // Standard Android settings page — works on every device since Android 6.
  return launch(
    `intent:#Intent;action=android.settings.IGNORE_BATTERY_OPTIMIZATION_SETTINGS;end`
  );
}

export function openAppDetails() {
  return launch(
    `intent:#Intent;action=android.settings.APPLICATION_DETAILS_SETTINGS;package=${APP_PKG};end`
  );
}

export function openAutostart(vendor) {
  // OEM-specific autostart whitelist pages. Intent components are the
  // canonical entry points published by each OEM for their "Auto start"
  // / "Background app permission" screens.
  const map = {
    xiaomi:
      'intent:#Intent;component=com.miui.securitycenter/com.miui.permcenter.autostart.AutoStartManagementActivity;end',
    oppo:
      'intent:#Intent;component=com.coloros.safecenter/.permission.startup.StartupAppListActivity;end',
    realme:
      'intent:#Intent;component=com.coloros.safecenter/.permission.startup.StartupAppListActivity;end',
    vivo:
      'intent:#Intent;component=com.vivo.permissionmanager/.activity.BgStartUpManagerActivity;end',
    oneplus:
      'intent:#Intent;component=com.oneplus.security/.chainlaunch.view.ChainLaunchAppListActivity;end',
    samsung:
      'intent:#Intent;component=com.samsung.android.lool/com.samsung.android.sm.ui.battery.BatteryActivity;end',
    honor:
      'intent:#Intent;component=com.huawei.systemmanager/.optimize.process.ProtectActivity;end',
  };
  const uri = map[vendor];
  if (!uri) return openAppDetails();
  return launch(uri);
}
